"use client";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Container } from "@/components/layout/container";

const values = [
  {
    title: "Sensibilité",
    text: "Je compose mes teintes comme des atmosphères, avec attention, intuition et recherche d’équilibre.",
  },
  {
    title: "Geste artisanal",
    text: "Chaque laine est teinte à la main par mes soins, dans une approche patiente, précise et profondément personnelle.",
  },
  {
    title: "Originalité",
    text: "J’aime créer des associations chaleureuses, vivantes et parfois festives, tout en gardant une vraie sobriété visuelle.",
  },
];

const monoFont = { fontFamily: '"IBM Plex Mono", monospace' };

const pagePalette = {
  background:
    "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
} as React.CSSProperties;

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0px)" : "translateY(28px)",
        transition: `opacity 700ms ease, transform 700ms ease ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

export default function QuiSuisJePage() {
  return (
    <div style={pagePalette}>
      <HeroSection />
      <StorySection />
      <ValuesSection />
      <ClosingSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="section overflow-hidden pt-8 sm:pt-10 lg:pt-12">
      <Container>
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-14">
          <Reveal className="max-w-[760px]">
            <p
              style={monoFont}
              className="text-[11px] uppercase tracking-[0.24em] text-[var(--accent)]/90"
            >
              Qui suis-je
            </p>

            <h1 className="mt-4 max-w-[11ch] text-5xl leading-[0.9] sm:text-6xl xl:text-7xl">
              Derrière chaque teinture, il y a mon regard, ma sensibilité et mon envie de créer.
            </h1>

            <p className="mt-6 max-w-[33rem] text-base leading-7 text-[var(--foreground)]/76 sm:text-lg">
              À travers Jour J Création, je partage une manière très personnelle
              d’aborder la laine, la couleur et la composition. Ma boutique est
              le prolongement de cet univers : chaleureux, artistique, singulier et vivant.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                href="/boutique"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Découvrir ma boutique
              </Link>

              <Link
                href="/sources-d-inspiration"
                className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-6 py-3 text-sm font-medium text-[var(--foreground)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Voir mes inspirations
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative mx-auto w-full max-w-[560px]">
              <div className="relative aspect-[4/4.8] overflow-hidden rounded-[28px] shadow-[var(--shadow)]">
                <Image
                  src="/images/6.webp"
                  alt="Portrait et univers Jour J Création"
                  fill
                  priority
                  sizes="(max-width: 1024px) 100vw, 42vw"
                  className="object-cover object-center"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(209,15,115,0.08)_0%,rgba(255,255,255,0)_30%,rgba(217,90,47,0.10)_100%)]" />
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function StorySection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal className="rounded-[28px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-4 shadow-[var(--shadow)]">
            <div className="relative aspect-[4/4.8] overflow-hidden rounded-[22px]">
              <Image
                src="/images/7.webp"
                alt="Univers Jour J Création"
                fill
                sizes="(max-width: 1024px) 100vw, 36vw"
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal delay={120} className="max-w-3xl">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              Mon univers
            </p>
            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Une boutique née d’un goût pour les teintes vibrantes, les matières délicates et les compositions sensibles.
            </h2>

            <div className="mt-6 space-y-5 text-base leading-7 text-[var(--foreground)]/74">
              <p>
                J’aime les couleurs chaudes, les contrastes subtils, les nuances qui
                vibrent ensemble sans jamais devenir agressives. Mon univers n’est pas
                figé : il oscille entre sobriété, fantaisie, douceur et éclat.
              </p>

              <p>
                À travers mes kits et mes pelotes, je cherche à proposer plus qu’un
                simple produit : un point de départ, une impulsion créative, un moment
                où l’on choisit la matière avec autant d’émotion que de précision.
              </p>

              <p>
                Jour J Création, c’est aussi cette idée simple : le bon moment pour
                choisir, recevoir, imaginer et commencer. Le jour où une création prend
                forme, chez vous, à partir d’un fil, d’une teinte, d’une envie.
              </p>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function ValuesSection() {
  return (
    <section className="section">
      <Container>
        <Reveal className="mb-10 max-w-2xl">
          <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Ce qui me guide
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Trois piliers qui donnent sa tonalité à ma boutique.
          </h2>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-3">
          {values.map((item, index) => (
            <Reveal key={item.title} delay={index * 90}>
              <article className="rounded-[26px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-6 shadow-[var(--shadow)]">
                <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]">
                Jour.J
                </p>
                <h3 className="mt-4 text-2xl leading-tight">{item.title}</h3>
                <p className="mt-4 text-sm leading-6 text-[var(--foreground)]/75">
                  {item.text}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function ClosingSection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="rounded-[34px] border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] p-8 shadow-[var(--shadow)] sm:p-10">
          <Reveal className="max-w-3xl">
            <p style={monoFont} className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
              Continuer la découverte
            </p>

            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Mon travail se raconte aussi dans les couleurs, les inspirations et les gestes de teinture.
            </h2>

            <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
              Si tu veux aller plus loin dans mon univers, tu peux découvrir mes sources
              d’inspiration, mon travail de teinturière et les spécificités de mes laines.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                href="/sources-d-inspiration"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Voir mes inspirations
              </Link>

              <Link
                href="/travail-de-teinturiere"
                className="rounded-full border border-[var(--border)] bg-[linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Découvrir mon travail
              </Link>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}